<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">扫码日志</h2>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="时间范围">
            <el-date-picker
              v-model="query.timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              :picker-options="pickerOptions"
              style="width: 360px"
            />
          </el-form-item>
          <el-form-item label="分享账号">
            <el-input
              v-model="query.share_user_id"
              placeholder="用户ID"
              clearable
              style="width: 120px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="扫码IP">
            <el-input
              v-model="query.scan_ip"
              placeholder="如 113.87.45.21"
              clearable
              style="width: 160px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <span class="range-tip">实际查询范围：{{ effectiveRange }}</span>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <el-table-column prop="scan_time" label="扫码时间" width="160" />
        <el-table-column label="分享人" width="200">
          <template slot-scope="scope">
            <span :class="{ 'cell-muted': !scope.row.share_user_name }">
              {{ scope.row.share_user_name || (scope.row.share_user_deleted === 1 ? '已注销用户' : '（无昵称）') }}
            </span>
            <span class="cell-uid">ID {{ scope.row.share_user_id }}</span>
          </template>
        </el-table-column>
        <el-table-column label="扫码IP" width="150">
          <template slot-scope="scope">
            {{ scope.row.scan_ip || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="设备信息（User-Agent）" min-width="400" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.user_agent || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <el-dialog title="扫码详情" :visible.sync="detailVisible" width="680px" append-to-body>
      <el-descriptions :column="2" size="small" border v-if="currentRow">
        <el-descriptions-item label="扫码时间">{{ currentRow.scan_time || '—' }}</el-descriptions-item>
        <el-descriptions-item label="扫码IP">{{ currentRow.scan_ip || '—' }}</el-descriptions-item>
        <el-descriptions-item label="分享账号ID">{{ currentRow.share_user_id }}</el-descriptions-item>
        <el-descriptions-item label="分享人">
          {{ currentRow.share_user_name || (currentRow.share_user_deleted === 1 ? '已注销用户' : '（无昵称）') }}
        </el-descriptions-item>
        <el-descriptions-item label="入库时间" :span="2">{{ currentRow.created_at || '—' }}</el-descriptions-item>
        <el-descriptions-item label="设备信息" :span="2">
          <pre class="ua-text">{{ currentRow.user_agent || '—' }}</pre>
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button size="small" @click="detailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getShareLogList } from '@/api/log'

export default {
  name: 'ShareLog',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: { timeRange: [], share_user_id: '', scan_ip: '' },
      page: 1,
      pageSize: 20,
      // 实际生效的时间范围（后端回显，未选时间时为默认的最近 7 天）
      rangeStart: '',
      rangeEnd: '',
      detailVisible: false,
      currentRow: null,
      pickerOptions: {
        shortcuts: [
          {
            text: '今天',
            onClick: (picker) => {
              const start = new Date()
              start.setHours(0, 0, 0, 0)
              picker.$emit('pick', [start, new Date()])
            }
          },
          {
            text: '最近 7 天',
            onClick: (picker) => {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 7 * 24 * 3600 * 1000)
              picker.$emit('pick', [start, end])
            }
          },
          {
            text: '最近 30 天',
            onClick: (picker) => {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 30 * 24 * 3600 * 1000)
              picker.$emit('pick', [start, end])
            }
          }
        ]
      }
    }
  },
  computed: {
    effectiveRange() {
      if (!this.rangeStart && !this.rangeEnd) {
        return '—'
      }
      return this.rangeStart + ' ~ ' + this.rangeEnd
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 列表（后端做筛选与分页；不传时间范围时后端默认最近 7 天）
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const range = this.query.timeRange || []
      if (range.length === 2) {
        params.start_date = range[0]
        params.end_date = range[1]
      }
      const shareUserId = String(this.query.share_user_id || '').trim()
      if (shareUserId) params.share_user_id = shareUserId
      const scanIp = (this.query.scan_ip || '').trim()
      if (scanIp) params.scan_ip = scanIp
      getShareLogList(params).then(res => {
        if (res.code !== 200) {
          this.list = []
          this.total = 0
          return
        }
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
        this.rangeStart = data.start_date || ''
        this.rangeEnd = data.end_date || ''
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    resetSearch() {
      this.query = { timeRange: [], share_user_id: '', scan_ip: '' }
      this.page = 1
      this.loadData()
    },
    handleDetail(row) {
      this.currentRow = row
      this.detailVisible = true
    }
  }
}
</script>

<style scoped>
.cell-muted {
  color: #c0c4cc;
}
.cell-uid {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}
.range-tip {
  font-size: 12px;
  color: #909399;
}
.ua-text {
  margin: 0;
  max-height: 160px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
  line-height: 1.6;
  background: #f7f8fa;
  padding: 8px 10px;
  border-radius: 4px;
}
</style>
